<app-list-loading [items]="$safeNavigationMigration(item?.values)">
    <div class="flex flex-col gap-5">
        <!-- Header: Category, Parameter & Search -->
        <div class="flex flex-row items-center gap-2">
            <div
                class="rounded-lg bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700"
            >
                {{ item?.category.name }}
            </div>
            <div
                class="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-600"
            >
                {{ item?.parameter.toUpperCase() }}
            </div>
            <div class="relative flex-grow">
                <i
                    class="fa-light fa-search absolute start-3 top-1/2 -translate-y-1/2 text-slate-400"
                ></i>
                <input
                    class="w-full rounded-lg border border-slate-200 py-2 !ps-9 pe-3 text-sm placeholder-slate-400 outline-none transition-all focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
                    [placeholder]="'translate_search_by_name' | translate"
                    [(ngModel)]="searchText"
                    aria-label="Search"
                />
            </div>
        </div>

        <!-- Value Comparison Cards -->
        <div class="grid grid-cols-2 gap-3">
            <!-- Aggregated value -->
            <div
                class="flex flex-col items-center gap-1 rounded-xl border p-4"
                [ngClass]="{
                    'border-rose-200 bg-rose-50':
                        aggregatedValue !== null &&
                        actualValue !== null &&
                        aggregatedValue !== actualValue,
                    'border-emerald-200 bg-emerald-50':
                        aggregatedValue !== null &&
                        actualValue !== null &&
                        aggregatedValue === actualValue,
                    'border-slate-200 bg-slate-50':
                        aggregatedValue === null || actualValue === null,
                }"
            >
                <span
                    class="text-xs font-medium uppercase tracking-wide"
                    [ngClass]="{
                        'text-rose-600':
                            aggregatedValue !== null &&
                            actualValue !== null &&
                            aggregatedValue !== actualValue,
                        'text-emerald-600':
                            aggregatedValue !== null &&
                            actualValue !== null &&
                            aggregatedValue === actualValue,
                        'text-slate-500':
                            aggregatedValue === null || actualValue === null,
                    }"
                >
                    {{ 'translate_aggregated_value' | translate }}
                </span>
                <span
                    class="text-2xl font-bold"
                    [ngClass]="{
                        'text-rose-700':
                            aggregatedValue !== null &&
                            actualValue !== null &&
                            aggregatedValue !== actualValue,
                        'text-emerald-700':
                            aggregatedValue !== null &&
                            actualValue !== null &&
                            aggregatedValue === actualValue,
                        'text-slate-700':
                            aggregatedValue === null || actualValue === null,
                    }"
                >
                    {{ aggregatedValue ?? 'N/A' }}
                </span>
            </div>

            <!-- Actual value -->
            <div
                class="flex flex-col items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-4"
            >
                <span
                    class="text-xs font-medium uppercase tracking-wide text-slate-500"
                >
                    {{ 'translate_actual_value' | translate }}
                </span>
                <span class="text-2xl font-bold text-slate-700">
                    {{ actualValue ?? 'N/A' }}
                </span>
            </div>
        </div>

        <!-- Values Table -->
        <div class="overflow-hidden rounded-xl border border-slate-200">
            <!-- Table Header -->
            <div
                class="grid items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3"
                [ngClass]="item?.period > 0 ? 'grid-cols-3' : 'grid-cols-2'"
            >
                <span
                    class="text-xs font-semibold uppercase tracking-wide text-slate-500"
                >
                    {{ 'translate_subcategory' | translate }}
                </span>
                <span
                    class="text-center text-xs font-semibold uppercase tracking-wide text-slate-500"
                >
                    @switch (item?.measurementCycle) {
                        @case ('annual') {
                            {{ 'translate_annual' | translate }}
                        }
                        @case ('semi_annual') {
                            {{ 'translate_year_half' | translate }}
                            {{ item?.period + 1 }}
                        }
                        @case ('quarter') {
                            {{ 'translate_quarter' | translate }}
                            {{ item?.period + 1 }}
                        }
                        @case ('month') {
                            {{ 'translate_month' | translate }}
                            {{ item?.period + 1 }}
                        }
                    }
                </span>
                @if (item?.period > 0) {
                    <span
                        class="text-center text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                        @switch (item?.measurementCycle) {
                            @case ('semi_annual') {
                                {{ 'translate_year_half' | translate }}
                            }
                            @case ('quarter') {
                                {{ 'translate_quarter' | translate }}
                            }
                            @case ('month') {
                                {{ 'translate_month' | translate }}
                            }
                        }
                        {{ item?.period }}
                    </span>
                }
            </div>

            <!-- Table Body -->
            <div class="divide-y divide-slate-100">
                @for (
                    value of $safeNavigationMigration(item?.values)
                        | filter: searchText : 'subcategory.name';
                    track value
                ) {
                    <div
                        class="hover:bg-slate-50/50 grid items-center gap-3 px-4 py-3 transition-colors"
                        [ngClass]="
                            item?.period > 0 ? 'grid-cols-3' : 'grid-cols-2'
                        "
                    >
                        <span class="text-sm text-slate-700">
                            {{ value.subcategory.name }}
                        </span>
                        <!-- Current Period (Editable) -->
                        <input
                            (keyup)="recalculateAggregatedValue()"
                            type="number"
                            [(ngModel)]="value.value"
                            class="w-full rounded-lg border border-slate-200 px-3 py-2 text-center text-sm outline-none transition-all focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
                        />
                        <!-- Previous Period (Read-only) -->
                        @if (item?.period > 0) {
                            <input
                                type="number"
                                [value]="getPreviousValue(value.subcategory.id)"
                                disabled
                                class="w-full cursor-not-allowed rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-center text-sm text-slate-400"
                            />
                        }
                    </div>
                }
            </div>
        </div>

        <!-- Submit Button -->
        <div class="flex justify-center border-t border-slate-100 pt-5">
            <button
                class="btn btn-primary"
                (click)="submit()"
                [disabled]="isSubmitting"
            >
                @if (isSubmitting) {
                    <app-loading-ring class="me-2"></app-loading-ring>
                }
                @if (!isSubmitting) {
                    <i class="fa-light fa-save me-2"></i>
                }
                <span>{{ 'translate_save' | translate }}</span>
            </button>
        </div>
    </div>
</app-list-loading>
